import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getGroups, getGroup, deleteReport, getReport, getReports, updateReport, getRegionsByGroup, setFavoriteReport, getCustomerReports, sendReport, } from 'utils/UnifiedViewWebAPIUtils';
export function useShipperReports({ userId }) {
    const query = useQuery({
        queryKey: ['getCustomerReports', userId],
        queryFn: getCustomerReports,
    });
    return query;
}
export function useRegions({ groupId }) {
    const query = useQuery({
        queryKey: ['getRegions', groupId],
        queryFn: getRegionsByGroup,
        enabled: !!groupId,
    });
    return query;
}
export function useGroup({ groupId }) {
    const query = useQuery({
        queryKey: ['getGroup', groupId],
        queryFn: getGroup,
        enabled: !!groupId,
    });
    const { data, error, isLoading, isError, isFetching, isSuccess, status } = query;
    return { data, error, isLoading, isError, isFetching, isSuccess, status };
}
export function useGroups() {
    const query = useQuery({
        queryKey: ['groups'],
        queryFn: getGroups,
    });
    const { data, error, isLoading, isError, isFetching, isSuccess, status } = query;
    // Use map to find groups by id faster
    const groupMap = new Map(data === null || data === void 0 ? void 0 : data.map((group) => [group._id, group]));
    const getGroupById = (id) => groupMap.get(id);
    return {
        data,
        error,
        isLoading,
        isError,
        isFetching,
        isSuccess,
        status,
        getGroupById,
    };
}
export const withGroups = (Component) => {
    return function WrapperComponent(props) {
        const groups = useGroups();
        return React.createElement(Component, Object.assign({}, props, { groups: groups }));
    };
};
export function useReport({ reportId }) {
    const query = useQuery({
        queryKey: ['reports', reportId],
        queryFn: getReport,
    });
    const { data, error, isLoading, isError, isSuccess, status } = query;
    return { data, error, isLoading, isError, isSuccess, status };
}
export function useReports({ filters }) {
    const query = useQuery({
        queryKey: ['reports', Object.assign({}, filters)],
        queryFn: getReports,
    });
    const { data, error, isLoading, isError, isSuccess, status } = query;
    return { data, error, isLoading, isError, isSuccess, status };
}
export function useDeleteReport({ filters, onSuccess, onError }) {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: deleteReport,
        onSuccess: (data, variables, context) => {
            queryClient.refetchQueries({
                queryKey: ['reports', Object.assign({}, filters)],
            });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, variables, context);
        },
        onError,
    });
    return { mutation };
}
export function useUpdateReport({ filters, onSuccess, onError }) {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: updateReport,
        onSuccess: (data, variables, context) => {
            queryClient.refetchQueries({
                queryKey: ['reports', Object.assign({}, filters)],
            });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, variables, context);
        },
        onError,
    });
    return { mutation };
}
export function useToggleFavoriteReport({ filters, onSuccess, onError }) {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: setFavoriteReport,
        // onMutate for optimistic update
        onMutate: async ({ id }) => {
            await queryClient.cancelQueries(['reports', Object.assign({}, filters)]);
            const previousReports = queryClient.getQueryData([
                'reports',
                Object.assign({}, filters),
            ]);
            queryClient.setQueryData(['reports', Object.assign({}, filters)], (oldData) => {
                return Object.assign(Object.assign({}, oldData), { items: oldData.items.map((report) => {
                        if (report._id === id) {
                            return Object.assign(Object.assign({}, report), { isFavorite: !report.isFavorite });
                        }
                        return report;
                    }) });
            });
            return { previousReports };
        },
        onSuccess: (data, variables, context) => {
            queryClient.refetchQueries({
                queryKey: ['reports', Object.assign({}, filters)],
            });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(data, variables, context);
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['reports', Object.assign({}, filters)], context.previousReports);
            onError === null || onError === void 0 ? void 0 : onError(error, variables, context);
        },
    });
    return { mutation };
}
export function useSendReport({ onSuccess, onError }) {
    const mutation = useMutation({
        mutationFn: sendReport,
        onSuccess,
        onError,
    });
    return { mutation };
}
